import React from 'react';
import { motion } from 'framer-motion';
import { Phone, CheckCircle, Car, Wrench, Trash2 } from 'lucide-react';

import HeroImage from './image/hero.png';
import KeyBenefitsImage from './image/keys.png';

const Home = () => {
  return (
    <div className="bg-gray-900 text-white font-sans">
      {/* Hero Section */}
      <main className="px-6 md:px-12 lg:px-24 py-16 md:py-24 flex flex-col md:flex-row items-center justify-between gap-12">
        <div className="md:w-1/2 text-center md:text-left">
          <motion.h1
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4"
          >
            Remorquage <br />
            Dépannage <br />
            Épaviste
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-lg mb-8"
          >
            <span className="font-bold">06.99.46.31.59</span>
          </motion.p>
          <motion.a
            href="tel:0699463159"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 px-6 py-3 rounded-full text-lg font-bold transition-colors whitespace-nowrap" // Ajout de whitespace-nowrap
          >
            Contactez-Nous Maintenant
          </motion.a>
        </div>
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="md:w-1/2 relative"
        >
          <img
            src={HeroImage}
            alt="Sev Remorque"
            className="rounded-lg shadow-2xl w-full"
          />
        </motion.div>
      </main>

      {/* Services Section */}
      <section className="px-6 md:px-12 lg:px-24 py-16">
        <h2 className="text-3xl font-bold text-center mb-12">Nos Services</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Service 1: Remorquage */}
          <div className="bg-gray-800 rounded-lg p-6 flex flex-col items-center justify-center">
            <Car className="w-12 h-12 text-yellow-500 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Remorquage Professionnel</h3>
            <CheckCircle className="w-6 h-6 text-green-500 mt-4" />
          </div>

          {/* Service 2: Dépannage */}
          <div className="bg-gray-800 rounded-lg p-6 flex flex-col items-center justify-center">
            <Wrench className="w-12 h-12 text-yellow-500 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Dépannage 24h/24 7j/7</h3>
            <CheckCircle className="w-6 h-6 text-green-500 mt-4" />
          </div>

          {/* Service 3: Épaviste */}
          <div className="bg-gray-800 rounded-lg p-6 flex flex-col items-center justify-center">
            <Trash2 className="w-12 h-12 text-yellow-500 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Épaviste agréé<br />Achat d'épave</h3>
            <CheckCircle className="w-6 h-6 text-green-500 mt-4" />
          </div>
        </div>
      </section>

      {/* Principaux Avantages Section */}
      <section className="bg-gray-900 py-16 md:py-24 px-6 md:px-12 lg:px-24">
        <h2 className="text-3xl font-bold text-center mb-12 text-white">Principaux Avantages</h2>
        <div className="relative">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            {/* Text Content */}
            <div className="space-y-6">
              {/* Avantage 1: Remorquage rapide */}
              <div className="flex items-start gap-4">
                <Car className="w-16 h-8 text-yellow-500" />
                <div>
                  <h3 className="text-xl font-semibold text-white">Remorquage rapide</h3>
                  <p className="text-gray-300">
                    Faites l'expérience d'un remorquage rapide et efficace de votre véhicule grâce à une
                    expertise professionnelle adaptée à vos besoins, garantissant un service rapide et
                    sans tracas.
                  </p>
                </div>
              </div>

              {/* Avantage 2: Évaluation précise */}
              <div className="flex items-start gap-4">
                <CheckCircle className="w-12 h-8 text-yellow-500" />
                <div>
                  <h3 className="text-xl font-semibold text-white">Évaluation précise</h3>
                  <p className="text-gray-300">
                    L'évaluation sur site garantit une évaluation précise du véhicule, ce qui vous aide à
                    prendre des décisions éclairées concernant vos voitures inutilisables.
                  </p>
                </div>
              </div>

              {/* Avantage 3: Assistance 24h/24 et 7j/7 */}
              <div className="flex items-start gap-4">
                <Phone className="w-12 h-8 text-yellow-500" />
                <div>
                  <h3 className="text-xl font-semibold text-white">Assistance 24h/24 et 7j/7</h3>
                  <p className="text-gray-300">
                    Des solutions orientées client 24h/24 et 7j/7 pour un traitement sans faille,
                    répondant aux urgences avec un soin spécialisé et un professionnalisme.
                  </p>
                </div>
              </div>

              {/* Avantage 4: Épaviste gratuit */}
              <div className="flex items-start gap-4">
                <Trash2 className="w-8 h-8 text-yellow-500" />
                <div>
                  <h3 className="text-xl font-semibold text-white">Épaviste gratuit</h3>
                  <p className="text-gray-300">
                    Enlèvement gratuit et évaluation sur place des véhicules inutilisables, prêt à
                    répondre à vos besoins.
                  </p>
                </div>
              </div>
            </div>

            {/* Image Section */}
            <div className="flex items-center justify-center">
              <img
                src={KeyBenefitsImage}
                alt="Sev Remorque"
                className="rounded-lg shadow-lg w-full max-w-md"
                style={{
                  aspectRatio: '1 / 1',
                  objectFit: 'contain', 
                  maxHeight: '400px',
                }}
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
